import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPlus, faFileInvoiceDollar } from '@fortawesome/free-solid-svg-icons';
import { useToast } from 'providers/ToastProvider';
import Button from 'components/base/Button';
import { useNFe, useExcluirDuplicata } from '../../../../hooks/useNFe';
import { DuplicataNFe } from '../../../../types/nfe';
import { formatDateBrazil } from '../../../../utils/dateUtils';
import ModalDuplicataNFe from './ModalDuplicataNFe';

// ── Local self-contained components ──────────────────────────────────────────

const BotaoAdicionarDuplicata: React.FC<{ nfeId: string }> = ({ nfeId }) => {
  const [show, setShow] = useState(false);
  return (
    <>
      <button
        type="button"
        className="btn btn-link btn-sm p-0 text-body-secondary text-decoration-none"
        style={{ fontSize: '0.78rem' }}
        onClick={() => setShow(true)}
      >
        <FontAwesomeIcon icon={faPlus} className="me-1" />
        Adicionar duplicata
      </button>
      <ModalDuplicataNFe show={show} onHide={() => setShow(false)} nfeId={nfeId} />
    </>
  );
};

const BotaoEditarDuplicata: React.FC<{ nfeId: string; duplicata: DuplicataNFe }> = ({
  nfeId, duplicata,
}) => {
  const [show, setShow] = useState(false);
  return (
    <>
      <Button variant="subtle-secondary" size="sm" onClick={() => setShow(true)} title="Editar">
        <FontAwesomeIcon icon={faEdit} />
      </Button>
      <ModalDuplicataNFe show={show} onHide={() => setShow(false)} nfeId={nfeId} duplicata={duplicata} />
    </>
  );
};

const BotaoExcluirDuplicata: React.FC<{ nfeId: string; duplicataId: string }> = ({
  nfeId, duplicataId,
}) => {
  const { showToast } = useToast();
  const mutation = useExcluirDuplicata(nfeId);

  const handleExcluir = async () => {
    if (!window.confirm('Deseja realmente excluir esta duplicata?')) return;
    try {
      await mutation.mutateAsync(duplicataId);
      showToast('Duplicata excluída com sucesso', 'success');
    } catch {
      // handled by interceptor
    }
  };

  return (
    <Button variant="subtle-danger" size="sm" onClick={handleExcluir} disabled={mutation.isPending} title="Excluir">
      <FontAwesomeIcon icon={faTrash} />
    </Button>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

interface SecaoDuplicatasProps {
  nfeId: string;
  isReadOnly: boolean;
}

const formatarMoeda = (valor?: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor ?? 0);

const SecaoDuplicatas: React.FC<SecaoDuplicatasProps> = ({ nfeId, isReadOnly }) => {
  const { data: nfe } = useNFe(nfeId);
  const duplicatas = nfe?.duplicatas ?? [];
  const total = duplicatas.reduce((acc, d) => acc + (d.valor || 0), 0);

  return (
    <>
      <div className="nfe-dest-section-header">
        <span className="nfe-dest-section-title">
          Duplicatas
          {duplicatas.length > 0 && (
            <span
              className="ms-2 badge bg-secondary-subtle text-secondary-emphasis"
              style={{ fontSize: '0.6rem', fontWeight: 700, verticalAlign: 'middle' }}
            >
              {duplicatas.length}
            </span>
          )}
        </span>
        {!isReadOnly && <BotaoAdicionarDuplicata nfeId={nfeId} />}
      </div>

      {duplicatas.length === 0 ? (
        <div className="nfe-empty-state">
          <FontAwesomeIcon icon={faFileInvoiceDollar} className="nfe-empty-state__icon" />
          <span className="nfe-empty-state__title">Nenhuma duplicata adicionada</span>
          {!isReadOnly && (
            <span className="nfe-empty-state__hint">
              Adicione duplicatas se a venda for parcelada
            </span>
          )}
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table nfe-table">
            <thead>
              <tr>
                <th>Número</th>
                <th className="text-center">Vencimento</th>
                <th className="text-end">Valor</th>
                {!isReadOnly && <th style={{ width: 80 }} />}
              </tr>
            </thead>
            <tbody>
              {duplicatas.map(duplicata => (
                <tr key={duplicata.id}>
                  <td className="text-body-emphasis fw-medium">{duplicata.numero}</td>
                  <td className="text-center text-body-secondary">
                    {formatDateBrazil(duplicata.dataVencimento)}
                  </td>
                  <td className="text-end fw-semibold text-primary-emphasis">
                    {formatarMoeda(duplicata.valor)}
                  </td>
                  {!isReadOnly && (
                    <td>
                      <div className="d-flex justify-content-end gap-1">
                        <BotaoEditarDuplicata nfeId={nfeId} duplicata={duplicata} />
                        <BotaoExcluirDuplicata nfeId={nfeId} duplicataId={duplicata.id} />
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td
                  colSpan={isReadOnly ? 2 : 3}
                  className="text-end text-body-secondary"
                  style={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.03em' }}
                >
                  TOTAL DAS DUPLICATAS
                </td>
                <td className="text-end fw-bold text-primary-emphasis">
                  {formatarMoeda(total)}
                </td>
                {!isReadOnly && <td />}
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </>
  );
};

export default SecaoDuplicatas;
